/**
 * PostHog initialisation for the student-facing app (PIX-551).
 *
 * The only module in the codebase that imports the PostHog SDK - everything
 * else talks to the facade in analytics.js. Mirrors sentry.js: gated on an
 * env var so local dev (where it is unset) never phones home, with the
 * decision logic it needs kept in separate pure modules so it stays testable.
 */
import posthog from 'posthog-js';

import { analytics } from './analytics.js';
import { resolveAttribution } from './attribution.js';
import { EVENTS, LANDING_ROUTES, PROPS } from './events.js';
import { applyBoardContextForSurface, anonymousBaseline } from './identity.js';
import { sanitiseEvent } from './redaction.js';
import { resolveSurface } from './surface.js';

const DEFAULT_API_HOST = 'https://us.i.posthog.com';
const UI_HOST = 'https://us.posthog.com';

/**
 * Initialise analytics for the given router. No-op when no project key is
 * configured (local dev). Call before `app.mount()`.
 */
export function initAnalytics(router) {
  const key = import.meta.env.VITE_POSTHOG_KEY;
  if (!key) {
    return;
  }

  posthog.init(key, {
    // Production points at our own subdomain (PostHog's managed reverse
    // proxy) so ad blockers and school DNS filters don't silently drop
    // events; staging talks to PostHog directly.
    api_host: import.meta.env.VITE_POSTHOG_HOST || DEFAULT_API_HOST,

    // Where the PostHog APP lives, as opposed to where events go. Must be
    // set explicitly whenever api_host is a proxy: the proxy only forwards
    // ingestion and knows nothing about the app, so without this the
    // toolbar tries to authenticate against our own subdomain and fails -
    // taking heatmap viewing with it. Captured data would be fine and
    // simply unviewable, which is the kind of failure nobody notices for
    // weeks. Harmless when api_host is already PostHog's own domain.
    ui_host: UI_HOST,

    // Off deliberately: autocapture roughly quadruples event volume, and
    // nothing we measure needs per-element clicks. Heatmaps do NOT depend on
    // it - they're a separate capture that doesn't count toward the bill.
    autocapture: false,
    enable_heatmaps: true,

    // Captured manually in the router below so the route NAME survives;
    // SPA autocapture only ever sees the URL. Page leave stays on - it's
    // what scrollmaps and exit-point analysis are built from.
    capture_pageview: false,
    capture_pageleave: true,

    // Anonymous landing visits have to become real people once a visitor
    // signs up, otherwise the landing-to-trial funnel has nothing to join on
    // and first-touch attribution is lost at the moment of conversion.
    person_profiles: 'always',

    // Strips bearer secrets that live in our URLs (password reset, upload
    // tokens) before anything leaves the browser. See redaction.js.
    //
    // `before_send` rather than the older `sanitize_properties`, which the SDK
    // now logs a deprecation error for. When that option is eventually
    // removed it will simply be ignored - no throw, no failing test, just
    // reset tokens quietly arriving at a third party - so it is not worth
    // waiting for a version bump to find out.
    before_send: sanitiseEvent,
  });

  analytics.attach(posthog);
  // Seed the anonymous baseline so landing events carry authentication and
  // subscription state from the very first pageview, not only after login.
  analytics.registerSuperProperties(anonymousBaseline());
  bindPageviews(router);
}

/**
 * Maintain per-navigation context and emit one pageview.
 *
 * The pageview carries the route name and the route *pattern* rather than the
 * resolved path - `/upload/:token` instead of `/upload/<a real token>`.
 * Grouping is better and no secret is captured.
 *
 * It also owns the two pieces of context that change with the route rather
 * than with the user: the surface (PIX-604) and, following from it, whether
 * student board context applies at all. Both are registered before the
 * pageview so the navigation's own first event is already correct.
 */
function bindPageviews(router) {
  router.afterEach((to) => {
    const matched = to.matched[to.matched.length - 1];
    const isLanding = LANDING_ROUTES.includes(to.name);

    // Surface is registered BEFORE the pageview is emitted, not after: this is
    // the first event of the navigation, and registering afterwards would tag
    // it with the surface of the route the user just left. Resolved from the
    // resolved path rather than the route pattern - the path is always present,
    // and it is never itself emitted, only classified.
    const surface = resolveSurface(to.path);
    analytics.registerSuperProperties({ [PROPS.SURFACE]: surface });

    // Board is student-only context that persists in browser storage, so it
    // has to be actively cleared on entering the panel and restored on the way
    // back out. See applyBoardContextForSurface.
    applyBoardContextForSurface(surface);

    // Attribution codes are registered as super properties on a landing
    // visit so they survive the visitor wandering the site before signing
    // up - by the time they reach the wizard the query string is long gone.
    if (isLanding) {
      const attribution = resolveAttribution(to.query);
      if (Object.keys(attribution).length) {
        analytics.registerSuperProperties(attribution);
      }
    }

    analytics.track(EVENTS.PAGE_VIEWED, {
      [PROPS.ROUTE_NAME]: to.name ?? null,
      [PROPS.ROUTE_PATH]: matched?.path ?? null,
      [PROPS.REQUIRES_AUTH]: Boolean(to.meta?.requiresAuth),
      [PROPS.IS_LANDING]: isLanding,
    });
  });
}
